import { sameMode, SeasonalTokenOverrides, SeasonConfig } from '../types';
import { BackgroundTint } from './background-tint';
import { FallingLeaves } from './falling-leaves';
import { FlyingGhosts } from './flying-ghosts';
import SpookyLogoDark from './images/logo-dark.png';
import SpookyLogoWhite from './images/logo-white.png';
import HauntedHouseSound from './sounds/HauntedHouse.mp3';

/**
 * Fall/Halloween Theme - Semantic Token Overrides
 *
 * Forces dark mode, so both default and _dark resolve to the same values.
 */
const fallTokenOverrides: SeasonalTokenOverrides = {
	colors: {
		primary: {
			fg: sameMode('#ea580c'),
			fgStrong: sameMode('#c2410c'),
			fgStronger: sameMode('#9a3412'),
			fgStrongest: sameMode('#7c2d12'),
			bg: sameMode('#ea580c'),
			bgHover: sameMode('#c2410c'),
			bgActive: sameMode('#9a3412'),
			bgAlpha: sameMode('rgba(234, 88, 12, 0.12)'),
			bgAlphaHover: sameMode('rgba(234, 88, 12, 0.20)'),
			bgAlphaActive: sameMode('rgba(234, 88, 12, 0.28)'),
		},
		bg: {
			canvas: sameMode('#0a0a0a'),
			surface: sameMode('#1a1a1a'),
			soft: sameMode('#2a2a2a'),
			subtle: sameMode('#1f1f1f'),
			topNav: sameMode('#0f0f0f'),
			inset: sameMode('#0d0d0d'),
		},
		text: {
			interactive: sameMode('#ea580c'),
		},
		border: {
			default: sameMode('#3a3a3a'),
			soft: sameMode('#333333'),
		},
	},
};

export const fallConfig: SeasonConfig = {
	themeClassName: 'halloween-theme',
	toggleEmoji: '🎃',

	logoLight: SpookyLogoWhite,
	logoDark: SpookyLogoDark,

	modalSound: HauntedHouseSound,
	modalSoundVolume: 0.3,

	semanticTokenOverrides: fallTokenOverrides,

	buttonTexts: [
		'🎃 Give re-boo-gnition',
		'🕯️ Summon appreciation',
		'🧙 Cast recognition',
		'🍬 Treat someone',
		'🦇 Send a Boo-nus',
		'🔮 Conjure recognition',
		'💀 Give re-cognition',
		'Conjure some kudos 🧙',
		'Spread spooky vibes 🦇',
		'Summon some praise 🌙',
	],

	modalDecorations: [
		{ emoji: '🎃', top: '10px', left: '20px', fontSize: '40px', opacity: 0.2, rotation: -15 },
		{ emoji: '🦇', top: '30px', right: '30px', fontSize: '30px', opacity: 0.15, rotation: 15 },
		{ emoji: '👻', bottom: '40px', left: '30px', fontSize: '25px', opacity: 0.15, rotation: -10 },
		{ emoji: '🍂', top: '60px', left: '60%', fontSize: '20px', opacity: 0.1, rotation: 25 },
	],

	modalColors: {
		overlayBg: 'blackAlpha.800',
		contentBg: 'black',
		borderColor: 'orange.500',
		boxShadow: '0 20px 60px rgba(255, 107, 53, 0.5)',
		logoDropShadowColor: 'rgba(255, 107, 53, 0.6)',
		subtitleColor: 'orange.200',
		enableButtonBg: 'orange.600',
		enableButtonHoverBg: 'orange.500',
		enableButtonActiveBg: 'orange.700',
		enableButtonBoxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
		enableButtonHoverBoxShadow: '0 8px 25px rgba(255, 107, 53, 0.6)',
		cancelButtonColor: 'orange.300',
		cancelButtonHoverColor: 'orange.200',
		noticeBg: 'orange.900',
		noticeBorderColor: 'orange.600',
		noticeTextColor: 'orange.200',
	},

	modalText: {
		subtitle: 'Transform your Bonusly experience with our spooky Halloween theme! 🎃',
		features: [
			{
				emoji: '🍂',
				text: 'Animated falling leaves, pumpkins, and friendly ghosts that dance across your screen',
			},
			{
				emoji: '🎨',
				text: 'Dark mode with orange accents and spooky vibes throughout the interface',
			},
			{
				emoji: '✨',
				text: 'Fun Halloween-themed recognition messages and seasonal surprises',
			},
		],
		endDateNotice: 'This spooky theme will be available through the end of October. Toggle it on/off anytime!',
		enableButtonText: 'Enable Halloween Theme',
		cancelButtonText: 'Maybe Later',
	},

	EffectsComponent: () => (
		<>
			<BackgroundTint />
			<FallingLeaves />
			<FlyingGhosts />
		</>
	),
};
